import React from 'react';
import {Table, Tooltip, message} from 'antd';
import {CopyOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {SorterSortingLogType} from '../graphql/queries/getSorterSortingLogListing';

interface SorterSortingLogColumnsProps {
    data: SorterSortingLogType[];
    loading: boolean;
}

const JsonCell: React.FC<{value?: string}> = ({value}) => {
    if (!value) return <span>-</span>;

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => {
            message.success('已复制', 1.5);
        });
    };

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
            <Tooltip title={<pre style={{maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{value}</pre>} placement="topLeft">
                <span style={{cursor: 'pointer', color: '#1677ff'}}>{value.length > 30 ? value.slice(0, 30) + '...' : value}</span>
            </Tooltip>
            <CopyOutlined
                onClick={handleCopy}
                style={{color: '#8c8c8c', cursor: 'pointer', flexShrink: 0}}
            />
        </div>
    );
};

const SorterSortingLogColumns: React.FC<SorterSortingLogColumnsProps> = ({data, loading}) => {
    const {t} = useTranslation();

    // 下标即后端 SourceType.code（见 dto/SorterType.kt#SourceType），新增来源类型时必须同步追加，
    // 否则落到 unknown 分支只显示「未知来源」
    const getSourceTypeName = (sourceType: number) => {
        const key = `wms.sorterSortingLog.sourceType.${
            [, 'getConfig', 'dwsScan', 'crossBeltScan', 'dropChute', 'lockChute', 'unlock', 'fullChute', 'runStatus', 'imageUpload', 'operatorUpdate', 'linearSorterLabelPrint', 'reprint', 'printBag', 'batchUnlock', 'batchLockChute', 'batchReset'][sourceType] || 'unknown'
        }`;
        return t(key);
    };

    const columns: ColumnsType<SorterSortingLogType> = [
        {
            title: t('wms.sorterSortingLog.columns.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('wms.sorterSortingLog.columns.sorterCode'),
            key: 'sorterCode',
            width: 140,
            render: (_, r) => r.sorterCode ? `${r.sorterCode}${r.sorterName ? ` - ${r.sorterName}` : ''}` : '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.sorterPlanName'),
            dataIndex: 'sorterPlanName',
            key: 'sorterPlanName',
            width: 160,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.sourceType'),
            dataIndex: 'sourceType',
            key: 'sourceType',
            width: 120,
            render: (v) => getSourceTypeName(v),
        },
        {
            title: t('wms.sorterSortingLog.columns.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: 160,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.bagNumber'),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            width: 120,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.expectSorterSortingChuteCodes'),
            dataIndex: 'expectSorterSortingChuteCodes',
            key: 'expectSorterSortingChuteCodes',
            width: 180,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.sorterSortingChuteCode'),
            dataIndex: 'sorterSortingChuteCode',
            key: 'sorterSortingChuteCode',
            width: 140,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.remark'),
            dataIndex: 'remark',
            key: 'remark',
            width: 160,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.operateAt'),
            dataIndex: 'operateAt',
            key: 'operateAt',
            width: 160,
            render: (v) => v ? dayjs.unix(v).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.createdBy'),
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 120,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.uuid'),
            dataIndex: 'uuid',
            key: 'uuid',
            width: 200,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.sortingTaskId'),
            dataIndex: 'sortingTaskId',
            key: 'sortingTaskId',
            width: 200,
            render: (v) => v || '-',
        },
        {
            title: t('wms.sorterSortingLog.columns.requestJson'),
            dataIndex: 'requestJson',
            key: 'requestJson',
            width: 160,
            render: (v) => <JsonCell value={v} />,
        },
        {
            title: t('wms.sorterSortingLog.columns.responseJson'),
            dataIndex: 'responseJson',
            key: 'responseJson',
            width: 160,
            render: (v) => <JsonCell value={v} />,
        },
        {
            title: t('wms.sorterSortingLog.columns.exceptionJson'),
            dataIndex: 'exceptionJson',
            key: 'exceptionJson',
            width: 160,
            render: (v) => <JsonCell value={v} />,
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{x: 'max-content'}}
            className="mb-4"
        />
    );
};

export default SorterSortingLogColumns;
